import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import {
  APP_DOWNLOAD_OPTIONS,
  isMobileDownloadContext,
} from "../../../Utils/appDownloadConfig";

const AppDownloadModal = ({ show, onHide }) => {
  const [isMobile, setIsMobile] = useState(() => isMobileDownloadContext());

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [show]);

  useEffect(() => {
    const updateViewport = () => {
      setIsMobile(isMobileDownloadContext());
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    if (show) {
      setIsMobile(isMobileDownloadContext());
    }
  }, [show]);

  const handleAppStoreClick = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!show) {
    return null;
  }

  return createPortal(
    <div
      className={`app-download-modal ${
        isMobile ? "app-download-modal--mobile" : "app-download-modal--desktop"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-download-modal-title"
    >
      <button
        type="button"
        className="app-download-modal__backdrop"
        onClick={onHide}
        aria-label="Close download modal"
      />

      <div className="app-download-modal__panel">
        <div className="app-download-modal__header">
          <div>
            <h2 id="app-download-modal-title" className="app-download-modal__title">
              {isMobile ? "Download Our Mobile Apps" : "Get the SimpleRenovate App"}
            </h2>
            <p className="app-download-modal__subtitle">
              {isMobile
                ? "Choose the app that fits you. Each app is built for a different role on SimpleRenovate."
                : "Scan the QR code with your phone to install the right app for your role."}
            </p>
          </div>
          <button
            type="button"
            className="app-download-modal__close"
            onClick={onHide}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="app-download-modal__body">
          {isMobile ? (
            <div className="app-download-modal__cards app-download-modal__cards--mobile">
              {APP_DOWNLOAD_OPTIONS.map((app) => (
                <div key={app.id} className="app-download-card app-download-card--mobile">
                  <span className="app-download-card__badge">{app.badge}</span>
                  <h3 className="app-download-card__title">{app.title}</h3>
                  <p className="app-download-card__description">{app.description}</p>

                  <ul className="app-download-card__features">
                    {app.features.map((feature) => (
                      <li key={feature}>
                        <TaskAltOutlinedIcon />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    className="app-download-card__store-btn"
                    onClick={() => handleAppStoreClick(app.appStoreUrl)}
                  >
                    <FileDownloadOutlinedIcon />
                    <span>
                      Download on the
                      <strong>App Store</strong>
                    </span>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="app-download-modal__desktop-note">
                <QrCodeScannerOutlinedIcon />
                <span>
                  Open your phone camera, scan the code, and install directly from
                  the App Store.
                </span>
              </div>

              <div className="app-download-modal__cards app-download-modal__cards--desktop">
                {APP_DOWNLOAD_OPTIONS.map((app) => (
                  <div
                    key={app.id}
                    className={`app-download-card app-download-card--desktop app-download-card--${app.id}`}
                  >
                    <div className="app-download-card__info">
                      <span className="app-download-card__badge">{app.badge}</span>
                      <h3 className="app-download-card__title">{app.title}</h3>
                      <p className="app-download-card__description">
                        {app.description}
                      </p>

                      <ul className="app-download-card__features">
                        {app.features.map((feature) => (
                          <li key={feature}>
                            <TaskAltOutlinedIcon />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="app-download-card__qr-panel">
                      <div className="app-download-card__qr-wrap">
                        <img src={app.qrImage} alt={`${app.title} QR code`} />
                      </div>
                      <p className="app-download-card__qr-title">
                        Scan QR code to download
                      </p>
                      <p className="app-download-card__qr-hint">
                        Point your camera at the QR code
                      </p>
                      <button
                        type="button"
                        className="app-download-card__store-link"
                        onClick={() => handleAppStoreClick(app.appStoreUrl)}
                      >
                        <FileDownloadOutlinedIcon />
                        <span>
                          Open in App Store
                          <strong>Apple iOS</strong>
                        </span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AppDownloadModal;
